import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Loader2 } from 'lucide-react';
import { dataViewApi } from '../../services/dataViewApi';
import { formatValue, isNumeric } from './tableUtils';

// Known cube prefixes to strip from column names
const CUBE_PREFIX_RE = /^(kpis|financial_measures|transaction_items|account_transactions_hierarchy|chart_of_accounts|account_hierarchy)\./;
// Time granularity suffixes to strip
const TIME_SUFFIX_RE = /\.(month|quarter|year|week|day|hour)$/;

// Drill-down columns to show in the panel table
const DRILL_DISPLAY_COLUMNS = [
  'transaction_items.transaction_date',
  'transaction_items.transaction_type',
  'transaction_items.document_number',
  'transaction_items.account_name',
  'transaction_items.account_type',
  'transaction_items.cust_name',
  'transaction_items.vend_name',
  'transaction_items.memo',
];

// All drill-down dimensions (includes hidden ID columns for deduplication)
const DRILL_ALL_DIMENSIONS = [
  'transaction_items.txn_id',
  'transaction_items.entry_sequence',
  ...DRILL_DISPLAY_COLUMNS,
];

function computeDateRange(dateValue, granularity) {
  if (!dateValue) return null;
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return null;

  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  const start = fmt(d);

  switch (granularity) {
    case 'day':
      return [start, start];
    case 'week': {
      const end = new Date(d);
      end.setDate(end.getDate() + 6);
      return [start, fmt(end)];
    }
    case 'month': {
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      return [start, fmt(end)];
    }
    case 'quarter': {
      const qEnd = new Date(d.getFullYear(), d.getMonth() + 3, 0);
      return [start, fmt(qEnd)];
    }
    case 'year': {
      return [start, `${d.getFullYear()}-12-31`];
    }
    default:
      return null;
  }
}

export default function DrillDownPanel({ open, cell, onClose, rfIds, parentTimeDimensions }) {
  const [transactions, setTransactions] = useState([]);
  const [drillColumns, setDrillColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [validDimensions, setValidDimensions] = useState(null);

  // Fetch transaction_items dimensions from Cube meta (once)
  useEffect(() => {
    if (validDimensions) return;
    (async () => {
      try {
        const DATA_ENGINE = window._env_?.REACT_APP_DATA_ENGINE_URL || 'http://localhost:8002';
        const r = await fetch(`${DATA_ENGINE}/api/cube/meta`);
        if (!r.ok) return;
        const json = await r.json();
        const cubes = json.data?.cubes || json.cubes || [];
        const tiCube = cubes.find(c => c.name === 'transaction_items');
        if (tiCube) {
          const dims = new Set(tiCube.dimensions.map(d => d.name.replace('transaction_items.', '')));
          setValidDimensions(dims);
        }
      } catch { /* ignore */ }
    })();
  }, [validDimensions]);

  // Execute drill-down query when cell changes
  useEffect(() => {
    if (!open || !cell?.row || !rfIds?.length) return;
    if (!validDimensions) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Build filters from row dimensions
        const filters = [];
        let dateValue = null;

        for (const [col, val] of Object.entries(cell.row)) {
          if (val == null || val === '') continue;
          if (typeof val === 'number' || (typeof val === 'string' && /^-?\d+(\.\d+)?$/.test(val.trim()))) continue;

          if (TIME_SUFFIX_RE.test(col) || col.toLowerCase().includes('transaction_date')) {
            dateValue = val;
            continue;
          }

          let dimName = col.replace(CUBE_PREFIX_RE, '');
          if (/\bid\b/.test(dimName) && dimName !== 'account_id') continue;

          if (!validDimensions.has(dimName)) continue;

          filters.push({
            member: `transaction_items.${dimName}`,
            operator: 'equals',
            values: [String(val)],
          });
        }

        // Build time dimension
        const parentTd = parentTimeDimensions?.[0] || {};
        const granularity = parentTd.granularity || null;
        let dateRange = parentTd.dateRange || null;

        if (dateValue && granularity) {
          const computed = computeDateRange(dateValue, granularity);
          if (computed) dateRange = computed;
        }

        // Build Cube query
        const query = {
          measures: ['transaction_items.total_amount'],
          dimensions: DRILL_ALL_DIMENSIONS,
          filters,
          order: { 'transaction_items.transaction_date': 'desc' },
          limit: 500,
        };
        if (dateRange) {
          query.timeDimensions = [{
            dimension: 'transaction_items.transaction_date',
            dateRange,
          }];
        }

        const result = await dataViewApi.executeDraft({
          rf_ids: rfIds,
          steps: [{ name: 'drilldown', query }],
        });

        if (!cancelled) {
          const sr = result?.step_results?.[0];
          if (sr?.status === 'ok') {
            const cols = (sr.columns || []).filter(c =>
              !c.includes('txn_id') && !c.includes('entry_sequence')
            );
            const allCols = sr.columns || [];
            const rows = (sr.rows || []).map(row =>
              Array.isArray(row) ? Object.fromEntries(allCols.map((c, i) => [c, row[i] ?? null])) : row
            );
            setDrillColumns(cols);
            setTransactions(rows);
            setTotalCount(sr.row_count || rows.length);
          } else {
            setError(sr?.error || 'Query failed');
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, cell, rfIds, validDimensions, parentTimeDimensions]);

  return (
    <AnimatePresence>
      {open && cell && (
        <motion.div
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed top-0 right-0 h-full w-[600px] bg-white border-l border-zinc-200 shadow-2xl z-50 flex flex-col"
        >
          {/* Panel header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
            <div>
              <div className="text-sm font-semibold text-zinc-900">Drill Down</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {cell.column}: <span className="font-medium text-zinc-700">{formatValue(cell.value)}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors"
            >
              <X size={14} />
            </button>
          </div>

          {/* Panel body */}
          <div className="flex-1 overflow-y-auto p-5">
            {/* Active filters */}
            {cell.row && (
              <div className="mb-4 bg-zinc-50 rounded-lg border border-zinc-200 p-3">
                <div className="text-[10px] text-zinc-400 mb-2 font-medium uppercase tracking-wide">Filters</div>
                <div className="space-y-1">
                  {Object.entries(cell.row)
                    .filter(([, v]) => v != null && typeof v === 'string' && isNaN(Number(v)))
                    .map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3 text-xs">
                      <span className="text-zinc-500 truncate">{k.replace(CUBE_PREFIX_RE, '').replace(TIME_SUFFIX_RE, '')}</span>
                      <span className="text-zinc-800 font-medium text-right">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-xs text-zinc-500 mb-3 font-medium uppercase tracking-wide">
              {loading ? 'Loading...' : `Transactions (${totalCount}${totalCount >= 500 ? '+' : ''})`}
            </div>

            {loading && (
              <div className="flex items-center gap-2 text-xs text-zinc-400 py-8 justify-center">
                <Loader2 size={14} className="animate-spin" /> Loading transactions...
              </div>
            )}

            {error && (
              <div className="text-xs text-red-500 bg-red-50 rounded-md p-3 border border-red-200">{error}</div>
            )}

            {!loading && !error && transactions.length === 0 && (
              <div className="text-xs text-zinc-400 text-center py-8">No transactions found</div>
            )}

            {!loading && transactions.length > 0 && (
              <div className="overflow-x-auto border border-zinc-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 sticky top-0">
                    <tr>
                      {drillColumns.map(col => (
                        <th key={col} className="px-2.5 py-2 text-left text-zinc-600 font-semibold whitespace-nowrap border-b border-zinc-200">
                          {col.replace('transaction_items.', '')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((row, i) => (
                      <tr key={i} className="border-t border-zinc-100 hover:bg-zinc-50">
                        {drillColumns.map(col => {
                          const val = row[col];
                          return (
                            <td key={col} className="px-2.5 py-1.5 text-zinc-700 whitespace-nowrap">
                              {formatValue(val, col.replace('transaction_items.', ''))}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
